"use client";

import { ArrowUpRight, Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function WhatADirectPartMarkContains() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "PRECISION DEPTH",
    icon: Blocks,
    description: "We ensure every mark is calibrated for longevity. Our laser systems penetrate the substrate with micron-level accuracy, creating permanent, high-contrast identification that withstands extreme industrial environments, chemical exposure, and mechanical wear."
  }, {
    number: "02",
    title: "MATERIAL INTEGRITY",
    icon: BrainCircuit,
    description: "Our process preserves the structural properties of your components. By utilizing advanced fiber laser technology, we achieve high-definition marking without compromising the heat-affected zone, ensuring your parts remain compliant with rigorous aerospace and medical standards."
  }, {
    number: "03",
    title: "TRACEABILITY COMPLIANCE",
    icon: Lock,
    description: "Full-cycle accountability is our baseline. From serialized data matrices to custom alphanumeric codes, our DPM solutions provide ironclad traceability. We deliver verified, machine-readable marks that meet global industry requirements for quality control and supply chain transparency."
  }];
  return <section data-section-id="5200" id="what-a-direct-part-mark-contains" className="bg-background text-foreground py-32 md:py-50 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs md:text-sm tracking-widest uppercase text-primary">
                02 / OUR METHODOLOGY
              </span>
              <div className="h-px w-16 bg-primary" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-black text-4xl sm:text-5xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tight uppercase">
              THREE /
              <br />
              CORE PILLARS.
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={i} pillar={pillar} Icon={Icon} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[480px] bg-card backdrop-blur-xl border border-border rounded-lg p-10 md:p-12 flex flex-col shadow-lg hover:border-primary hover:shadow-2xl transition-all duration-500">
        {}
        <div className="font-mono text-5xl md:text-6xl font-light text-primary transition-transform duration-500 group-hover:scale-110 origin-left">
          {pillar.number}
        </div>

        {}
        <div className="h-px w-full bg-border mt-6 mb-10 group-hover:bg-primary transition-colors duration-500" />

        {}
        <Icon className="size-8 text-foreground/70 group-hover:text-primary transition-colors duration-500 mb-8" strokeWidth={1} />

        {}
        <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight mb-6 leading-tight">
          {pillar.title}
        </h3>

        {}
        <p className="font-default text-base md:text-[17px] leading-relaxed text-foreground/75 mb-10 flex-grow">
          {pillar.description}
        </p>

        {}
        
      </Card>
    </div>;
}